import type {
  ConditionKind,
  DateBound,
  DateFilter,
  MatchListValue,
  NumberPredicate,
  TreeConditionNode,
  TreeGroupNode,
  TreeNode,
} from '@/components/tree-dnd/tree-dnd-types';
import {
  isConditionNode,
  isGroupNode,
} from '@/components/tree-dnd/tree-dnd-types';
import type {
  DateBoundDto,
  DateFilterDto,
  FilterNodeDto,
  ItemsFilterDto,
  NumberFilterDto,
} from '@/generated/korza-api-client/types.gen';

const UNSUPPORTED_KINDS = new Set<ConditionKind>(['rfmGroup', 'location']);

export class AudienceFilterMappingError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'AudienceFilterMappingError';
  }
}

function mapItems(value: MatchListValue): ItemsFilterDto {
  return {
    mode: value.match,
    ids: value.ids,
  };
}

export function mapNumberPredicate(value: NumberPredicate): NumberFilterDto {
  return { type: value.kind, value: value.value };
}

function mapDateBound(bound: DateBound): DateBoundDto {
  if (bound.type === 'relative') {
    return {
      type: 'relative',
      offset: bound.offset,
      unit: bound.unit,
      timezone: bound.timezone,
    };
  }
  if (bound.type === 'absolute') {
    return { type: 'absolute', date: bound.date };
  }
  return { type: 'epoch' };
}

function mapDateFilter(when: DateFilter): DateFilterDto {
  switch (when.type) {
    case 'anytime':
      return { type: 'anytime' };
    case 'absolute':
      return {
        type: 'absolute',
        direction: when.direction,
        date: when.date,
      };
    case 'relative':
      return {
        type: 'relative',
        direction: when.direction,
        date: mapDateBound(when.date),
      };
    case 'window':
      return {
        type: 'window',
        from: mapDateBound(when.from),
        to: mapDateBound(when.to),
      };
    default: {
      const _exhaustive: never = when;
      throw new AudienceFilterMappingError(
        `Unsupported date filter: ${JSON.stringify(_exhaustive)}`
      );
    }
  }
}

/** Map a single UI condition node to an API FilterNodeDto (includes negation). */
export function mapConditionNodeToFilterDto(
  node: TreeConditionNode
): FilterNodeDto {
  return mapCondition(node);
}

function mapCondition(node: TreeConditionNode): FilterNodeDto {
  if (UNSUPPORTED_KINDS.has(node.kind)) {
    throw new AudienceFilterMappingError(
      `Filter condition "${node.kind}" is not supported by the API yet`
    );
  }

  let mapped: FilterNodeDto;

  switch (node.kind) {
    case 'installedApp':
      mapped = {
        type: 'condition',
        kind: 'appDownloadDate',
        params: { date: mapDateFilter(node.args.when) },
      };
      break;
    case 'boughtProduct':
      mapped = {
        type: 'condition',
        kind: 'boughtProducts',
        params: {
          products: mapItems(node.args.products),
          date: mapDateFilter(node.args.when),
        },
      };
      break;
    case 'receivedNotification':
      mapped = {
        type: 'condition',
        kind: 'receivedCampaign',
        params: { campaigns: mapItems(node.args.campaigns) },
      };
      break;
    case 'clickedCampaign':
      mapped = {
        type: 'condition',
        kind: 'clickedCampaign',
        params: { campaigns: mapItems(node.args.campaigns) },
      };
      break;
    case 'lastNotificationAt':
      mapped = {
        type: 'condition',
        kind: 'lastNotificationAt',
        params: { date: mapDateFilter(node.args.when) },
      };
      break;
    case 'lastAppAccessAt':
      mapped = {
        type: 'condition',
        kind: 'lastAppAccessAt',
        params: { date: mapDateFilter(node.args.when) },
      };
      break;
    case 'viewedProduct':
      mapped = {
        type: 'condition',
        kind: 'viewedProduct',
        params: {
          products: mapItems(node.args.products),
          date: mapDateFilter(node.args.when),
        },
      };
      break;
    case 'lastOrder':
      mapped = {
        type: 'condition',
        kind: 'lastOrder',
        params: { date: mapDateFilter(node.args.when) },
      };
      break;
    case 'orderFrequency':
      mapped = {
        type: 'condition',
        kind: 'orderFrequency',
        params: {
          count: mapNumberPredicate(node.args.count),
          date: mapDateFilter(node.args.when),
        },
      };
      break;
    case 'totalSpend':
      mapped = {
        type: 'condition',
        kind: 'totalSpend',
        params: { amount: mapNumberPredicate(node.args.amount) },
      };
      break;
    case 'hasNotificationsEnabled':
      mapped = {
        type: 'condition',
        kind: 'hasNotificationsEnabled',
        params: {},
      };
      break;
    default:
      throw new AudienceFilterMappingError(
        `Filter condition "${node.kind}" is not supported by the API yet`
      );
  }

  return node.negated ? { type: 'not', child: mapped } : mapped;
}

function mapGroup(node: TreeGroupNode): FilterNodeDto {
  if (node.children.length === 0) {
    throw new AudienceFilterMappingError('Filter groups must contain children');
  }

  const children = node.children.map(mapNode);
  const mapped: FilterNodeDto =
    node.operator === 'AND'
      ? { type: 'and', children }
      : { type: 'or', children };

  return node.negated ? { type: 'not', child: mapped } : mapped;
}

function mapNode(node: TreeNode): FilterNodeDto {
  if (isGroupNode(node)) return mapGroup(node);
  if (isConditionNode(node)) return mapCondition(node);
  const _exhaustive: never = node;
  throw new AudienceFilterMappingError(
    `Unknown tree node: ${JSON.stringify(_exhaustive)}`
  );
}

/** Map the UI tree-dnd root to an API FilterNodeDto. */
export function mapTreeToFilterNodeDto(root: TreeGroupNode): FilterNodeDto {
  return mapGroup({ ...root, negated: false });
}
